import React, { useEffect, useState } from 'react';
import {
    Box,
    Card,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
    Chip,
    IconButton,
    Tooltip,
    CircularProgress,
    TablePagination
} from '@mui/material';
import AssignmentIcon from '@mui/icons-material/Assignment';
import PndApi from 'src/utils/pnd/PndRestApi';
import { OPERATION_TYPE_FOR_DROP_OFF_ASSIGNED, OPERATION_TYPE_FOR_PICKUP_ASSIGNED } from 'src/utils/pnd/PndConstants';
import { formatDate } from 'src/utils/DateUtils';
import Badge from "react-bootstrap/Badge";
import FreeRiderDetails from 'src/containers/PnD/Order/OrderCreation/View/components/FreeRiderComponents/FreeRiderDetails';

const ReAssignOrderGrid = ({ selectedHubId }) => {
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    // Assign Modal States
    const [assignModalOpen, setAssignModalOpen] = useState(false);
    const [selectedOrder, setSelectedOrder] = useState(null);
    const [selectedDriverId, setSelectedDriverId] = useState(null);

    useEffect(() => {
        fetchOrders();
    }, [selectedHubId]);

    const fetchOrders = async () => {
        try {
            setLoading(true);
            const response = await PndApi.GetAllAssignedOrders({
                orderType: 0,
                orderPriorityTypeId: 0,
                hubId: selectedHubId
            });
            if (response.status === 200 && response.data.status === 200) {
                setOrders(response.data.data || []);
            } else {
                setOrders([]);
            }
        } catch (error) {
            console.error('Error fetching assigned orders:', error);
            setOrders([]);
        } finally {
            setLoading(false);
        }
    };

    const handleReAssignClick = (order) => {
        setSelectedOrder(order);
        setAssignModalOpen(true);
    };

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const getOrderTypeBadge = (orderType) => {
        if (orderType === 1) return <Badge bg="success" style={{ fontSize: '10px' }}>DO</Badge>;
        if (orderType === 2) return <Badge bg="primary" style={{ fontSize: '10px' }}>PU</Badge>;
        return <Badge bg="warning" style={{ fontSize: '10px' }}>PnD</Badge>;
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
                <CircularProgress sx={{ color: '#32CA73' }} />
            </Box>
        );
    }

    const paginatedOrders = orders.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

    return (
        <Box sx={{ width: '100%' }}>
            <TableContainer component={Card} sx={{ boxShadow: 'none', borderRadius: 1, border: '1px solid #e2e8f0' }}>
                <Table size="small">
                    <TableHead sx={{ backgroundColor: '#f8fafc' }}>
                        <TableRow>
                            <TableCell sx={{ fontWeight: 'bold', fontSize: '12px' }}>#</TableCell>
                            <TableCell sx={{ fontWeight: 'bold', fontSize: '12px' }}>ORDER NO</TableCell>
                            <TableCell sx={{ fontWeight: 'bold', fontSize: '12px' }}>ERP NO</TableCell>
                            <TableCell sx={{ fontWeight: 'bold', fontSize: '12px' }}>TYPE</TableCell>
                            <TableCell sx={{ fontWeight: 'bold', fontSize: '12px' }}>RIDER</TableCell>
                            <TableCell sx={{ fontWeight: 'bold', fontSize: '12px' }}>SCHEDULED</TableCell>
                            <TableCell sx={{ fontWeight: 'bold', fontSize: '12px' }}>STATUS</TableCell>
                            <TableCell align="center" sx={{ fontWeight: 'bold', fontSize: '12px' }}>ACTIONS</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {paginatedOrders.length > 0 ? paginatedOrders.map((order, index) => (
                            <TableRow key={order.orderId} sx={{ '&:hover': { backgroundColor: '#f1f5f9' } }}>
                                <TableCell sx={{ fontSize: '11px', py: 0.5 }}>{page * rowsPerPage + index + 1}</TableCell>
                                <TableCell sx={{ fontSize: '11px', fontWeight: 'bold', py: 0.5 }}>{order.orderNumber}</TableCell>
                                <TableCell sx={{ fontSize: '11px', py: 0.5 }}>{order.erpOrderNo || '—'}</TableCell>
                                <TableCell sx={{ py: 0.5 }}>{getOrderTypeBadge(order.orderType)}</TableCell>
                                <TableCell sx={{ fontSize: '11px', py: 0.5 }}>{order.riderName || 'N/A'}</TableCell>
                                <TableCell sx={{ fontSize: '11px', py: 0.5 }}>{formatDate(order.scheduledDateTimeSlot)}</TableCell>
                                <TableCell sx={{ py: 0.5 }}>
                                    <Chip label={order.orderStatus} size="small" color="primary" variant="outlined" sx={{ fontSize: '10px', height: '20px' }} />
                                </TableCell>
                                <TableCell align="center" sx={{ py: 0.5 }}>
                                    <Tooltip title="Re-Assign Order">
                                        <IconButton size="small" color="info" onClick={() => handleReAssignClick(order)} sx={{ p: 0.5 }}>
                                            <AssignmentIcon sx={{ fontSize: 18 }} />
                                        </IconButton>
                                    </Tooltip>
                                </TableCell>
                            </TableRow>
                        )) : (
                            <TableRow>
                                <TableCell colSpan={8} align="center" sx={{ py: 4, color: '#999' }}>
                                    No assigned orders found for re-assignment
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
                <TablePagination
                    rowsPerPageOptions={[5, 10, 25]}
                    component="div"
                    count={orders.length}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                />
            </TableContainer>

            {assignModalOpen && selectedOrder && (
                <FreeRiderDetails
                    modalShow={assignModalOpen}
                    setModalShow={setAssignModalOpen}
                    isOrderId={selectedOrder.orderId}
                    isOrderNo={selectedOrder.orderNumber}
                    isOrderType={selectedOrder.orderType}
                    isRiderId={selectedOrder.riderId}
                    isOperationType={selectedOrder.orderType === 1 ? OPERATION_TYPE_FOR_DROP_OFF_ASSIGNED : OPERATION_TYPE_FOR_PICKUP_ASSIGNED}
                    setRedirect={() => fetchOrders()} // Refresh after re-assign
                    setSelectedDriverId={setSelectedDriverId}
                    selectedDriverId={selectedDriverId}
                />
            )}
        </Box>
    );
};

export default ReAssignOrderGrid;
